import { DataSource } from '@angular/cdk/collections';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import { Observable, of as observableOf, merge } from 'rxjs';

// TODO: Replace this with your own data model type
export interface ClientsListItem {
  commonName: string;
  legalName: string;
  id: number;
}


/**
 * Data source for the ClientsList view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class ClientsListDataSource extends DataSource<ClientsListItem> {
  data: ClientsListItem[];
  paginator: MatPaginator;
  sort: MatSort;
  nonFilteredData;

  constructor(private clients: Array<any>) {
    super();
    this.data = clients;
    this.nonFilteredData = clients;
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<ClientsListItem[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations = [
      observableOf(this.data),
      this.paginator.page,
      this.sort.sortChange
    ];

    return merge(...dataMutations).pipe(map(() => {
      return this.getPagedData(this.getSortedData([...this.data]));
    }));
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() { }
  filter(field: any, fullData: any) {
    let filterData: any[] = fullData;
    if (field['epicorIdFilter']) {
      filterData = filterData.filter(bp => {
        return bp.externalCustomerId?.toLocaleLowerCase().indexOf(field['epicorIdFilter'].toLocaleLowerCase()) > -1;
      });
    }
    if (field['commonNameFilter']) {
      filterData = filterData.filter(bp => {
        return bp.commonName?.toLocaleLowerCase().indexOf(field['commonNameFilter'].toLocaleLowerCase()) > -1;
      });
    }
    if (field['legalNameFilter']) {
      filterData = filterData.filter(bp => {
        return bp.legalName?.toLocaleLowerCase().indexOf(field['legalNameFilter'].toLocaleLowerCase()) > -1;
      });
    }
    return filterData;
  }

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: ClientsListItem[]) {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.splice(startIndex, this.paginator.pageSize);
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: any[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'commonName': return compare(a.commonName, b.commonName, isAsc);
        case 'id': return compare(+a.externalCustomerId, +b.externalCustomerId, isAsc);
        case 'legalName': return compare(a.legalName, b.legalName, isAsc);
        default: return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
